import { Box } from "@mui/material"
import React, {
  ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"

import type { TokenOption } from "../pages/Swap"
import { commify } from "../utils"
import { formatBNToString } from "../utils"
import styles from "./SearchSelect.module.scss"
import { useTranslation } from "react-i18next"
import CustomCard from "./CustomCard"
import Text from "./Text"
import Button from "./Button"
import { useIsMobile } from "src/hooks/useIsMobile"

interface Props {
  tokensData: TokenOption[]
  onSelect: (symbol: string) => void
  value?: string
}

export default function SearchSelect({
  tokensData,
  onSelect,
}: Props): ReactElement {
  const [searchTerm, setSearchTerm] = useState("")
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const focusedItemRef = useRef<HTMLLIElement>(null)
  const isMobile = useIsMobile()
  const { t } = useTranslation()

  useEffect(() => {
    if (inputRef?.current != null) {
      inputRef.current.focus()
    }
  }, [inputRef])

  useEffect(() => {
    // scroll active li into view if user got there via keyboard nav
    if (focusedItemRef?.current != null) {
      focusedItemRef.current.scrollIntoView()
    }
  }, [focusedItemRef, activeIndex])

  const filteredTokens = useMemo(() => {
    // filter tokens by user input
    return tokensData.filter(({ symbol, name, address }) => {
      const target = searchTerm.toLowerCase()
      return (
        symbol.toLowerCase().includes(target) ||
        name.toLowerCase().includes(target) ||
        address === searchTerm
      )
    })
  }, [tokensData, searchTerm])

  const lastSelectableIndex = useMemo(() => {
    // find the last idx of isAvailable tokens
    const lastAvailableIndex =
      filteredTokens.findIndex(({ isAvailable }) => isAvailable === false) - 1
    return lastAvailableIndex < 0
      ? filteredTokens.length - 1
      : lastAvailableIndex
  }, [filteredTokens])

  const handleConfirm = () => {
    if (activeIndex != null) {
      const token = filteredTokens[activeIndex]
      if (token.isAvailable) {
        onSelect(token.symbol)
      }
    }
  }

  return (
    <div className={styles.searchSelect}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        p={2}
        border="1px solid rgba(255,255,255,0.2)"
        borderRadius="10px"
        height={54}
        className={styles.inputContainer}
      >
        <input
          placeholder={
            isMobile ? t("Search") : t("Search by name, address, or symbol")
          }
          value={searchTerm}
          onChange={(e) => {
            const inputValue = e.target.value
            const nextActiveIndex = inputValue === "" ? null : 0
            activeIndex !== nextActiveIndex && setActiveIndex(nextActiveIndex)
            setSearchTerm(inputValue)
          }}
          ref={inputRef}
          onFocus={() => activeIndex != null && setActiveIndex(null)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && activeIndex != null) {
              const token = filteredTokens[activeIndex]
              token.isAvailable && onSelect(token.symbol)
            } else if (e.key === "ArrowUp") {
              setActiveIndex((prev) =>
                prev === null || prev === 0 ? null : Math.max(0, prev - 1),
              )
            } else if (e.key === "ArrowDown") {
              setActiveIndex((prev) =>
                prev === null ? 0 : Math.min(lastSelectableIndex, prev + 1),
              )
            }
          }}
        />
      </Box>
      <Box my={2}>
        {filteredTokens.length === 0 ? (
          <Text>{t("No Tokens Found.")}</Text>
        ) : (
          filteredTokens.map((token, i) => (
            <CustomCard
              variant="darkest-gray"
              key={token.symbol}
              p={1}
              mb={1}
              withGradient={i === activeIndex}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                ref={i === activeIndex ? focusedItemRef : null}
                onClick={() => setActiveIndex(i)}
                sx={{ cursor: "pointer" }}
                mx={1}
              >
                <Box display="flex" alignItems="center">
                  <img src={token.icon} height={20} />
                  <Text ml={2}>{token.symbol}</Text>
                </Box>
                <Text ml={2} sx={{ wordBreak: "break-word" }}>
                  {commify(formatBNToString(token.amount, token.decimals))}
                </Text>
              </Box>
            </CustomCard>
          ))
        )}
      </Box>
      <Button disabled={activeIndex === null} onClick={handleConfirm} fullWidth>
        {t("Confirm")}
      </Button>
    </div>
  )
}
